<template>
  <div class="analysis-page">
    <!-- 헤더 영역 (필터 포함) -->
    <header class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <span class="icon">📊</span>
          운행 분석
        </h1>
      </div>

      <!-- 필터 영역 (헤더 내부) -->
      <div class="header-filters">
        <div class="filter-item">
          <input v-model="startDate" type="date" class="filter-input" />
        </div>
        <span class="filter-separator">~</span>
        <div class="filter-item">
          <input v-model="endDate" type="date" class="filter-input" />
        </div>
        <div class="filter-item">
          <select v-model="selectedDeviceId" class="filter-select">
            <option value="">전체 장비</option>
            <option
              v-for="device in devices"
              :key="device.value"
              :value="device.value"
            >
              {{ device.label }}
            </option>
          </select>
        </div>
        <button class="filter-btn" :disabled="loading" @click="onApplyFilters">
          <span v-if="loading" class="spinner" />
          {{ loading ? "조회중..." : "🔍 조회" }}
        </button>
      </div>

      <nav class="header-nav">
        <button class="nav-btn" @click="goOilSupply">
          <span class="nav-icon">⛽</span>
          주유 통계
        </button>
        <button class="nav-btn" @click="goBim">
          <span class="nav-icon">🏗️</span>
          진척도
        </button>
        <button class="nav-btn" @click="goMap">
          <span class="nav-icon">🗺️</span>
          지도
        </button>
        <button class="nav-btn" @click="openManual">
          <span class="nav-icon">📘</span>
          사용자 매뉴얼
        </button>
      </nav>
    </header>

    <!-- 에러 메시지 -->
    <div v-if="error" class="error-alert">
      <span class="error-icon">⚠️</span>
      {{ error }}
    </div>

    <!-- 메인 대시보드 -->
    <main class="dashboard-main">
      <!-- 상단 요약 카드들 -->
      <div class="summary-row">
        <!-- 오늘·월간·연간 요약 -->
        <article class="stat-card wide">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">📅</span>
              기간별 운행 요약
            </h2>
            <span class="card-badge">실시간</span>
          </div>
          <div v-if="runSummaryRows.length" class="data-table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>장비명</th>
                  <th class="num">오늘</th>
                  <th class="num">월간</th>
                  <th class="num">연간</th>
                  <th class="num">오늘 최고속도</th>
                  <th class="num">월간 최고속도</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in runSummaryRows" :key="index">
                  <td class="equip-name">
                    {{ row.equipmentName || row.deviceId || "-" }}
                  </td>
                  <td class="num highlight">
                    {{ formatDistance(row.todayDistance) }}
                  </td>
                  <td class="num">{{ formatDistance(row.monthDistance) }}</td>
                  <td class="num">{{ formatDistance(row.yearDistance) }}</td>
                  <td class="num speed">
                    {{ formatSpeed(row.todayMaxSpeed) }}
                  </td>
                  <td class="num speed">
                    {{ formatSpeed(row.monthMaxSpeed) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>

      <!-- 통계 카드 그리드 -->
      <div class="stats-grid">
        <!-- 운행 통계 요약 -->
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">🚜</span>
              운행 통계 요약
            </h2>
          </div>
          <p class="card-desc">기간 내 총 운행거리, 정차, 속도 요약</p>
          <div v-if="summaryRows.length" class="data-table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>장비</th>
                  <th class="num">총거리</th>
                  <th class="num">정차</th>
                  <th class="num">평균속도</th>
                  <th class="num">최고속도</th>
                  <th class="num">이동률</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in summaryRows" :key="index">
                  <td class="equip-name">
                    {{ row.equipmentName || row.deviceId || "-" }}
                  </td>
                  <td class="num highlight">
                    {{ formatDistance(row.totalDistance) }}
                  </td>
                  <td class="num">{{ formatNumber(row.totalStayCount) }}</td>
                  <td class="num">{{ formatSpeed(row.avgSpeed) }}</td>
                  <td class="num">{{ formatSpeed(row.maxSpeed) }}</td>
                  <td class="num percent">
                    {{ formatPercent(row.movingRatio) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>

        <!-- 장비별 가동률 -->
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">⚙️</span>
              장비별 가동률
            </h2>
          </div>
          <p class="card-desc">조회 기간 대비 실제 운행 일수</p>
          <div v-if="utilizationRows.length" class="data-table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>장비</th>
                  <th class="num">총일수</th>
                  <th class="num">운행일</th>
                  <th class="num">가동률</th>
                  <th class="num">총거리</th>
                  <th class="num">일평균</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in utilizationRows" :key="index">
                  <td class="equip-name">
                    {{ row.equipmentName || row.deviceId || "-" }}
                  </td>
                  <td class="num">{{ formatNumber(row.totalDays) }}</td>
                  <td class="num">{{ formatNumber(row.activeDays) }}</td>
                  <td class="num percent highlight">
                    {{ formatPercent(row.utilizationRate) }}
                  </td>
                  <td class="num">{{ formatDistance(row.totalDistance) }}</td>
                  <td class="num">
                    {{ formatDistance(row.avgDailyDistance) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>

      <!-- 일별 운행 통계 (전체 너비) -->
      <article class="stat-card full-width">
        <div class="card-title-bar">
          <h2 class="card-title">
            <span class="title-icon">📈</span>
            일별 운행 통계
          </h2>
          <span class="card-badge secondary">{{ dailyRows.length }}건</span>
        </div>
        <p class="card-desc">날짜별 거리·속도 추이</p>
        <div v-if="dailyRows.length" class="data-table-wrap scrollable">
          <table class="data-table">
            <thead>
              <tr>
                <th>날짜</th>
                <th>장비</th>
                <th class="num">총거리</th>
                <th class="num">정차</th>
                <th class="num">평균속도</th>
                <th class="num">최고속도</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in dailyRows" :key="index">
                <td class="date-cell">{{ row.date }}</td>
                <td class="equip-name">{{ row.equipmentName || "-" }}</td>
                <td class="num highlight">
                  {{ formatDistance(row.totalDistance) }}
                </td>
                <td class="num">{{ formatNumber(row.totalStayCount) }}</td>
                <td class="num">{{ formatSpeed(row.avgSpeed) }}</td>
                <td class="num">{{ formatSpeed(row.maxSpeed) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state">
          <span class="empty-icon">📭</span>
          <p>데이터가 없습니다.</p>
        </div>
      </article>

      <!-- 장비 상세 분석 (2열 그리드) -->
      <div class="detail-grid">
        <!-- 시간대별 운행 패턴 (그래프) -->
        <article class="stat-card" :class="{ inactive: !selectedDeviceId }">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">🕐</span>
              시간대별 운행 패턴
            </h2>
          </div>
          <p class="card-desc">선택한 장비의 0~23시 운행 분포</p>
          <div v-if="!selectedDeviceId" class="empty-state info">
            <span class="empty-icon">👆</span>
            <p>장비를 선택하면 조회됩니다.</p>
          </div>
          <div v-else-if="hourlyPattern.length" class="chart-container">
            <v-chart :option="hourlyChartOption" autoresize />
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>

        <!-- 주요 정차 위치 (탭 + 지도) -->
        <article class="stat-card" :class="{ inactive: !selectedDeviceId }">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">📍</span>
              주요 정차 위치
            </h2>
            <div
              v-if="selectedDeviceId && stayLocations.length"
              class="tab-btns"
            >
              <button
                class="tab-btn"
                :class="{ active: stayTab === 'map' }"
                @click="stayTab = 'map'"
              >
                🗺️ 지도
              </button>
              <button
                class="tab-btn"
                :class="{ active: stayTab === 'list' }"
                @click="stayTab = 'list'"
              >
                📋 목록
              </button>
            </div>
          </div>
          <p class="card-desc">정차 빈도 상위 위치 (10m 이내 클러스터)</p>
          <div v-if="!selectedDeviceId" class="empty-state info">
            <span class="empty-icon">👆</span>
            <p>장비를 선택하면 조회됩니다.</p>
          </div>
          <div v-else-if="stayLocations.length">
            <!-- 지도 탭 -->
            <div v-show="stayTab === 'map'" class="stay-map-container">
              <div ref="stayMap" class="stay-map" />
            </div>
            <!-- 목록 탭 -->
            <div v-show="stayTab === 'list'" class="data-table-wrap scrollable">
              <table class="data-table compact">
                <thead>
                  <tr>
                    <th>위도</th>
                    <th>경도</th>
                    <th class="num">정차</th>
                    <th class="num">시간(분)</th>
                    <th class="num">방문</th>
                    <th>마지막</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in stayLocations" :key="index">
                    <td class="coord">{{ formatCoord(row.latitude) }}</td>
                    <td class="coord">{{ formatCoord(row.longitude) }}</td>
                    <td class="num highlight">
                      {{ formatNumber(row.totalStayCount) }}
                    </td>
                    <td class="num">
                      {{ formatNumber(row.estimatedStayTimeMinutes, 1) }}
                    </td>
                    <td class="num">{{ formatNumber(row.visitCount) }}</td>
                    <td class="datetime">
                      {{ formatDateTime(row.lastVisitTime) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>
    </main>
  </div>
</template>

<script>
import RtkApi from "../api/osm/RtkApi";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { MAPBOX_ACCESS_TOKEN, MAPBOX_MAP_STYLE_SATELLITE } from "../const";

use([
  CanvasRenderer,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
]);

export default {
  name: "RtkAnalysis",
  components: {
    VChart,
  },
  data() {
    return {
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      selectedDeviceId: "",
      devices: [],
      summaryRows: [],
      dailyRows: [],
      utilizationRows: [],
      runSummaryRows: [],
      hourlyPattern: [],
      stayLocations: [],
      loading: false,
      error: null,
      stayTab: "map",
      stayMap: null,
      stayMarkers: [],
      hourlyChartOption: null,
    };
  },
  computed: {
    bimId() {
      return parseInt(this.$route?.params?.id) || 1;
    },
  },
  async mounted() {
    await this.loadDevices();
    await this.onApplyFilters();
  },
  beforeDestroy() {
    this.destroyStayMap();
  },
  methods: {
    async loadDevices() {
      try {
        // RTK 운행 요약에서 deviceId 기반 장비 목록 가져오기
        const response = await RtkApi.getEquipmentRunSummary();
        const items = this.normalizeList(response);
        this.devices = items.map((item, index) => {
          const label =
            item.equipmentName ||
            item.carNo ||
            item.serialNumber ||
            item.deviceId ||
            `장비-${index + 1}`;
          const value = item.deviceId;
          return { label, value };
        });
      } catch (error) {
        console.error("장비 목록 불러오기 실패", error);
        this.devices = [];
      }
    },
    async onApplyFilters() {
      if (!this.startDate || !this.endDate) {
        this.error = "기간을 선택하세요.";
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([
          this.fetchSummary(),
          this.fetchDaily(),
          this.fetchUtilization(),
          this.fetchRunSummary(),
          this.fetchHourlyPattern(),
          this.fetchStayLocations(),
        ]);
      } catch (error) {
        console.error("분석 데이터 조회 실패", error);
        this.error = "데이터를 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },
    async fetchSummary() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedDeviceId
        ? await RtkApi.getStatisticsSummaryByDevice(
            this.selectedDeviceId,
            params
          )
        : await RtkApi.getStatisticsSummary(params);
      this.summaryRows = this.normalizeList(response);
    },
    async fetchDaily() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedDeviceId
        ? await RtkApi.getStatisticsDailyByDevice(this.selectedDeviceId, params)
        : await RtkApi.getStatisticsDaily(params);
      this.dailyRows = this.normalizeList(response);
    },
    async fetchUtilization() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = await RtkApi.getStatisticsUtilization(params);
      this.utilizationRows = this.normalizeList(response);
    },
    async fetchRunSummary() {
      const response = await RtkApi.getEquipmentRunSummary();
      this.runSummaryRows = this.normalizeList(response);
    },
    async fetchHourlyPattern() {
      if (!this.selectedDeviceId) {
        this.hourlyPattern = [];
        this.hourlyChartOption = null;
        return;
      }
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = await RtkApi.getStatisticsHourlyPattern(
        this.selectedDeviceId,
        params
      );
      this.hourlyPattern = this.normalizeList(response);
      if (this.hourlyPattern.length) {
        this.renderHourlyChart();
      }
    },
    async fetchStayLocations() {
      if (!this.selectedDeviceId) {
        this.stayLocations = [];
        this.destroyStayMap();
        return;
      }
      const params = {
        startDate: this.startDate,
        endDate: this.endDate,
        limit: 10,
      };
      const response = await RtkApi.getStayLocations(
        this.selectedDeviceId,
        params
      );
      this.stayLocations = this.normalizeList(response);
      if (this.stayLocations.length) {
        this.renderStayMap();
      }
    },
    normalizeList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (payload.content) {
        return Array.isArray(payload.content)
          ? payload.content
          : [payload.content];
      }
      if (payload.data) {
        return Array.isArray(payload.data) ? payload.data : [payload.data];
      }
      // 단일 객체인 경우 배열로 감싸서 반환
      if (typeof payload === "object" && payload !== null) {
        return [payload];
      }
      return [];
    },
    getEquipmentName(deviceId) {
      if (!deviceId) return "-";
      const device = this.deviceOptions.find((d) => d.deviceId === deviceId);
      return device ? device.name : deviceId;
    },
    formatDistance(meters) {
      if (meters === null || meters === undefined || isNaN(meters)) return "-";
      const km = Number(meters) / 1000;
      if (km >= 100) return `${km.toFixed(0)} km`;
      return `${km.toFixed(1)} km`;
    },
    formatSpeed(speed) {
      if (speed === null || speed === undefined || isNaN(speed)) return "-";
      return `${Number(speed).toFixed(1)} km/h`;
    },
    formatPercent(value) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return `${Number(value).toFixed(1)} %`;
    },
    formatNumber(value, digits = 0) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },
    formatCoord(value) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return Number(value).toFixed(5);
    },
    formatDateTime(value) {
      if (!value) return "-";
      return value.replace("T", " ");
    },
    getDefaultStartDate() {
      const date = new Date();
      date.setDate(date.getDate() - 6);
      return this.formatDateInput(date);
    },
    getDefaultEndDate() {
      return this.formatDateInput(new Date());
    },
    formatDateInput(date) {
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    goBim() {
      // 강제 새로고침으로 BIM 모드로 이동
      window.location.hash = `/dashboard/${this.bimId}`;
      window.location.reload();
    },
    goMap() {
      // 강제 새로고침으로 지도 모드로 이동
      window.location.hash = `/dashboard/${this.bimId}/map`;
      window.location.reload();
    },
    goOilSupply() {
      // 주유 통계 페이지로 이동
      window.location.hash = `/dashboard/${this.bimId}/oil-supply`;
      window.location.reload();
    },
    openManual() {
      window.open("/manual/manual.html", "_blank");
    },
    // 시간대별 차트 옵션 설정
    renderHourlyChart() {
      const labels = this.hourlyPattern.map((d) => `${d.hour}시`);
      const distances = this.hourlyPattern.map((d) =>
        ((d.totalDistance || 0) / 1000).toFixed(2)
      );

      this.hourlyChartOption = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(15, 23, 42, 0.95)",
          borderColor: "rgba(255,255,255,0.1)",
          textStyle: { color: "#e2e8f0" },
          formatter: (params) => {
            const p = params[0];
            return `${p.name}<br/>운행 거리: <strong>${p.value}</strong> km`;
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "10%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } },
          axisLabel: { color: "#94a3b8", fontSize: 10 },
        },
        yAxis: {
          type: "value",
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } },
          axisLabel: {
            color: "#94a3b8",
            fontSize: 10,
            formatter: "{value} km",
          },
          splitLine: { lineStyle: { color: "rgba(255,255,255,0.05)" } },
        },
        series: [
          {
            name: "운행 거리",
            type: "bar",
            data: distances,
            itemStyle: {
              color: "rgba(59, 130, 246, 0.8)",
              borderRadius: [4, 4, 0, 0],
            },
            emphasis: {
              itemStyle: { color: "rgba(96, 165, 250, 1)" },
            },
          },
        ],
      };
    },
    // 정차 위치 지도 렌더링
    renderStayMap() {
      this.$nextTick(() => {
        if (!this.$refs.stayMap || !this.stayLocations.length) return;
        this.destroyStayMap();

        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

        // 중심점 계산
        const lats = this.stayLocations.map((l) => l.latitude);
        const lngs = this.stayLocations.map((l) => l.longitude);
        const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
        const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

        this.stayMap = new mapboxgl.Map({
          container: this.$refs.stayMap,
          style: MAPBOX_MAP_STYLE_SATELLITE,
          center: [centerLng, centerLat],
          zoom: 14,
        });

        this.stayMap.on("load", () => {
          this.addStayMarkers();
        });
      });
    },
    addStayMarkers() {
      if (!this.stayMap) return;
      // 기존 마커 제거
      this.stayMarkers.forEach((m) => m.remove());
      this.stayMarkers = [];

      // 최대 정차 횟수 (크기 스케일 용)
      const maxCount = Math.max(
        ...this.stayLocations.map((l) => l.totalStayCount || 1)
      );

      this.stayLocations.forEach((loc, idx) => {
        const count = loc.totalStayCount || 0;
        const size = 20 + (count / maxCount) * 10; // 20~30px (기존 20~50px)

        const el = document.createElement("div");
        el.className = "stay-marker";
        el.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          background: rgba(239, 68, 68, 0.8);
          border: 2px solid #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        `;
        el.textContent = idx + 1;

        const stayTime = (loc.estimatedStayTimeMinutes || 0).toFixed(1);
        const visitCnt = loc.visitCount || 0;
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div style="padding: 8px; font-size: 12px;">
            <div style="font-weight: 600; margin-bottom: 4px;">#${
              idx + 1
            } 정차 위치</div>
            <div>정차 횟수: <strong>${count}</strong>회</div>
            <div>체류 시간: <strong>${stayTime}</strong>분</div>
            <div>방문 횟수: <strong>${visitCnt}</strong>회</div>
          </div>
        `);

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([loc.longitude, loc.latitude])
          .setPopup(popup)
          .addTo(this.stayMap);

        this.stayMarkers.push(marker);
      });

      // 모든 마커가 보이도록 bounds 조정
      if (this.stayLocations.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        this.stayLocations.forEach((loc) => {
          bounds.extend([loc.longitude, loc.latitude]);
        });
        this.stayMap.fitBounds(bounds, { padding: 50, maxZoom: 16 });
      }
    },
    destroyStayMap() {
      this.stayMarkers.forEach((m) => m.remove());
      this.stayMarkers = [];
      if (this.stayMap) {
        this.stayMap.remove();
        this.stayMap = null;
      }
    },
  },
};
</script>

<style scoped>
/* ===== 페이지 레이아웃 ===== */
.analysis-page {
  min-height: 100vh;
  padding: 32px 40px 60px;
  background: linear-gradient(135deg, #0a1628 0%, #132a3f 100%);
  color: #e2e8f0;
}

/* ===== 헤더 ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
  white-space: nowrap;
}

.page-title .icon {
  font-size: 24px;
}

/* ===== 헤더 내 필터 ===== */
.header-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: center;
  flex-wrap: wrap;
}

.filter-separator {
  color: #64748b;
  font-weight: 500;
}

.header-filters .filter-item {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.header-filters .filter-input,
.header-filters .filter-select {
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #f1f5f9;
  font-size: 13px;
  transition: all 0.2s ease;
  min-width: 130px;
}

.header-filters .filter-input:focus,
.header-filters .filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.header-filters .filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.header-filters .filter-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
}

.header-filters .filter-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.header-nav {
  display: flex;
  gap: 10px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.nav-icon {
  font-size: 16px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== 에러 알림 ===== */
.error-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #fca5a5;
  font-size: 14px;
  margin-bottom: 24px;
}

.error-icon {
  font-size: 20px;
}

/* ===== 대시보드 메인 ===== */
.dashboard-main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ===== 카드 그리드 ===== */
.summary-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

/* ===== 통계 카드 ===== */
.stat-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.stat-card.wide {
  grid-column: span 2;
}

.stat-card.full-width {
  width: 100%;
}

.stat-card.inactive {
  opacity: 0.5;
}

.card-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

.title-icon {
  font-size: 22px;
}

.card-badge {
  padding: 4px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-badge.secondary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
}

.card-desc {
  margin: 0 0 20px;
  font-size: 13px;
  color: #94a3b8;
}

/* ===== 데이터 테이블 ===== */
.data-table-wrap {
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.data-table-wrap.scrollable {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table th {
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.4);
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.data-table th.num {
  text-align: center;
}

.data-table td {
  padding: 14px 16px;
  font-size: 14px;
  color: #e2e8f0;
  text-align: center;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.data-table tbody tr {
  transition: background 0.2s ease;
}

.data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table td.num {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.data-table td.highlight {
  color: #60a5fa;
  font-weight: 600;
}

.data-table td.percent {
  color: #34d399;
}

.data-table td.speed {
  color: #fbbf24;
}

.data-table td.equip-name {
  font-weight: 600;
  color: #ffffff;
}

.data-table td.date-cell {
  color: #94a3b8;
  font-weight: 500;
}

.data-table td.time-cell {
  color: #a78bfa;
  font-weight: 600;
}

.data-table td.coord {
  font-family: "SF Mono", "Consolas", monospace;
  font-size: 12px;
  color: #94a3b8;
}

.data-table td.datetime {
  font-size: 12px;
  color: #94a3b8;
}

.data-table.compact th,
.data-table.compact td {
  padding: 12px 14px;
}

/* ===== 빈 상태 ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-state.info {
  background: rgba(59, 130, 246, 0.05);
  border-radius: 12px;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  color: #94a3b8;
}

/* ===== 차트 컨테이너 ===== */
.chart-container {
  height: 280px;
  padding: 16px 8px;
}

/* ===== 탭 버튼 ===== */
.tab-btns {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px;
}

.tab-btn {
  padding: 6px 12px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #e2e8f0;
}

.tab-btn.active {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

/* ===== 정차 위치 지도 ===== */
.stay-map-container {
  height: 320px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.stay-map {
  width: 100%;
  height: 100%;
}

/* Mapbox 팝업 스타일 오버라이드 */
:deep(.mapboxgl-popup-content) {
  background: rgba(15, 23, 42, 0.95) !important;
  color: #e2e8f0 !important;
  border-radius: 8px !important;
  padding: 0 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

:deep(.mapboxgl-popup-close-button) {
  color: #94a3b8 !important;
  font-size: 18px !important;
  padding: 4px 8px !important;
}

:deep(.mapboxgl-popup-close-button:hover) {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

:deep(.mapboxgl-popup-tip) {
  border-top-color: rgba(15, 23, 42, 0.95) !important;
}

/* ===== 반응형 ===== */
@media (max-width: 1200px) {
  .stats-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .stat-card.wide {
    grid-column: span 1;
  }
}

@media (max-width: 1024px) {
  .header-filters {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    margin-top: 12px;
  }
}

@media (max-width: 768px) {
  .analysis-page {
    padding: 20px 16px 40px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-filters {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  .header-filters .filter-item {
    flex: 1;
    min-width: 120px;
  }

  .header-filters .filter-input,
  .header-filters .filter-select {
    width: 100%;
  }

  .header-filters .filter-btn {
    width: 100%;
  }

  .header-nav {
    width: 100%;
  }

  .nav-btn {
    flex: 1;
    justify-content: center;
  }

  .page-title {
    font-size: 20px;
  }

  .card-title {
    font-size: 16px;
  }
}
</style>
